import React, { useState, useEffect } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { ACCENT_COLORS } from '../../config/dashboardTemplates';
import { useAdhanPlayer } from '../../hooks/useAdhanPlayer';

// Import reusable components for data logic
import SimpleTimeCard from '../simpledashboard/SimpleTimeCard';
import SimpleDateCard from '../simpledashboard/SimpleDateCard';
import SimplePrayerCountdownCard from '../simpledashboard/SimplePrayerCountdownCard';
import SimplePrayerTimesCard from '../simpledashboard/SimplePrayerTimesCard';

const LuxuryClockTemplate = ({ 
  prayerTimes,
  dateTime,
  duas,
  currentDuaIndex,
  onDateChange,
}) => {
  const { settings } = useSettings();
  const accentColor = ACCENT_COLORS[settings.simpleDashboard?.accentColor || 'emerald'];
  const [currentTime, setCurrentTime] = useState(new Date());

  useAdhanPlayer(prayerTimes, settings);

  // Update time for analog clock
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Generate hour markers
  const generateHourMarkers = () => {
    const markers = [];
    for (let i = 0; i < 12; i++) {
      const rotation = i * 30;
      markers.push(
        <div
          key={`hour-${i}`}
          className="absolute w-1 h-10 bg-gradient-to-b from-yellow-600 to-yellow-500 top-5 left-1/2 transform -translate-x-1/2 rounded-sm shadow-lg"
          style={{
            transformOrigin: '50% 250px',
            transform: `translateX(-50%) rotate(${rotation}deg)`,
            boxShadow: '0 0 10px rgba(184, 134, 11, 0.5)',
          }}
        />
      );
    }
    return markers;
  };

  // Generate minute markers
  const generateMinuteMarkers = () => {
    const markers = [];
    for (let i = 1; i < 60; i++) {
      if (i % 5 !== 0) { // Skip hour positions
        const rotation = i * 6;
        markers.push(
          <div
            key={`minute-${i}`}
            className="absolute w-0.5 h-5 bg-white/40 top-7 left-1/2 transform -translate-x-1/2 rounded-sm"
            style={{
              transformOrigin: '50% 240px',
              transform: `translateX(-50%) rotate(${rotation}deg)`,
            }}
          />
        );
      }
    }
    return markers;
  };

  // Calculate clock hand positions
  const getClockHandPositions = () => {
    const hours = currentTime.getHours() % 12;
    const minutes = currentTime.getMinutes();
    const seconds = currentTime.getSeconds();

    return {
      hour: (hours * 30) + (minutes * 0.5),
      minute: minutes * 6,
      second: seconds * 6,
    };
  };

  const handPositions = getClockHandPositions();

  // Get next prayer for highlighting
  const getNextPrayer = () => {
    if (!prayerTimes?.times || Object.keys(prayerTimes.times).length === 0) return null;
    
    const now = new Date();
    const times = Object.entries(prayerTimes.times);
    
    for (const [prayer, time] of times) {
      if (new Date(`${now.toDateString()} ${time}`) > now) {
        return prayer;
      }
    }
    return times[0]?.[0] || 'Fajr';
  };

  const nextPrayer = getNextPrayer();

  return (
    <div className="h-screen w-full relative text-white overflow-hidden bg-gradient-to-br from-gray-900 via-black to-gray-800">
      {/* Subtle background gradients */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-600/8 rounded-full blur-3xl"></div>
      </div>

      {/* Main Grid Layout */}
      <div className="relative h-full grid grid-cols-3 grid-rows-3 gap-10 p-10 z-10">
        
        {/* Top Left - Current Time Panel */}
        <div className="info-panel backdrop-blur-md bg-black/80 border border-white/10 rounded-3xl p-8 shadow-2xl">
          <div className="text-center">
            <h3 className="text-yellow-500 text-lg font-semibold mb-5 tracking-wide font-serif">Current Time</h3>
            <SimpleTimeCard 
              render={({ time }) => (
                <div className="text-white text-3xl font-light mb-3 tracking-widest font-serif">
                  {time}
                </div>
              )}
            />
            <SimpleDateCard 
              dateTime={dateTime}
              onDateChange={onDateChange}
              render={({ gregorian, hijri }) => (
                <div className="text-white/70 text-sm font-light leading-relaxed">
                  <div>{gregorian}</div>
                  <div className="mt-2">{hijri}</div>
                </div>
              )}
            />
          </div>
        </div>

        {/* Top Right - Prayer Times Panel */}
        <div className="info-panel backdrop-blur-md bg-black/80 border border-white/10 rounded-3xl p-8 shadow-2xl">
          <h3 className="text-yellow-500 text-lg font-semibold mb-5 text-center tracking-wide font-serif">Prayer Times</h3>
          <SimplePrayerTimesCard
            prayerTimes={prayerTimes}
            settings={settings}
            render={({ prayers }) => (
              <div className="space-y-3">
                {prayers.map(([prayer, time]) => {
                  const isNext = prayer === nextPrayer;
                  return (
                    <div key={prayer} className="flex justify-between items-center text-sm">
                      <span className={`${isNext ? 'text-yellow-500 font-medium' : 'text-white/80'}`}>
                        {prayer}
                      </span>
                      <span className={`${isNext ? 'text-yellow-500 font-medium' : 'text-white/80'}`}>
                        {time}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          />
        </div>

        {/* Center - Main Clock */}
        <div className="row-span-3 col-start-2 row-start-1 flex items-center justify-center">
          <div className="relative w-[600px] h-[600px] rounded-full bg-gradient-to-br from-gray-800 to-black shadow-2xl border-2 border-yellow-600/30">
            {/* Clock Face */}
            <div 
              className="absolute inset-6 rounded-full border-2 border-yellow-600/30"
              style={{
                background: `
                  radial-gradient(circle at center, #1a1a1a 0%, #0f0f0f 100%),
                  conic-gradient(from 0deg, 
                    rgba(184, 134, 11, 0.1) 0deg, 
                    rgba(16, 185, 129, 0.1) 90deg,
                    rgba(184, 134, 11, 0.1) 180deg,
                    rgba(16, 185, 129, 0.1) 270deg,
                    rgba(184, 134, 11, 0.1) 360deg)
                `
              }}
            >
              {/* Hour Markers */}
              {generateHourMarkers()}
              
              {/* Minute Markers */}
              {generateMinuteMarkers()}

              {/* Geometric Pattern */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 opacity-10 animate-spin" style={{ animationDuration: '120s' }}>
                <svg width="400" height="400" viewBox="0 0 400 400" className="text-white">
                  <defs>
                    <pattern id="islamicPattern" patternUnits="userSpaceOnUse" width="100" height="100">
                      <polygon points="50,10 90,50 50,90 10,50" fill="none" stroke="currentColor" strokeWidth="1"/>
                      <circle cx="50" cy="50" r="25" fill="none" stroke="currentColor" strokeWidth="1"/>
                    </pattern>
                  </defs>
                  <rect width="400" height="400" fill="url(#islamicPattern)" opacity="0.3"/>
                </svg>
              </div>

              {/* Clock Hands */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                {/* Hour Hand */}
                <div
                  className="absolute w-2 h-40 bg-gradient-to-t from-yellow-600 to-white rounded-full shadow-lg"
                  style={{
                    transformOrigin: '50% 100%',
                    transform: `rotate(${handPositions.hour}deg)`,
                    top: '-160px',
                    left: '-4px',
                    boxShadow: '0 0 15px rgba(218, 165, 32, 0.6)',
                  }}
                />
                
                {/* Minute Hand */}
                <div
                  className="absolute w-1.5 h-48 bg-gradient-to-t from-yellow-600 to-white rounded-full shadow-lg"
                  style={{
                    transformOrigin: '50% 100%',
                    transform: `rotate(${handPositions.minute}deg)`,
                    top: '-192px',
                    left: '-3px',
                    boxShadow: '0 0 15px rgba(218, 165, 32, 0.6)',
                  }}
                />
                
                {/* Second Hand */}
                <div
                  className="absolute w-0.5 h-52 bg-gradient-to-t from-emerald-500 to-white rounded-full shadow-lg"
                  style={{
                    transformOrigin: '50% 100%',
                    transform: `rotate(${handPositions.second}deg)`,
                    top: '-208px',
                    left: '-1px',
                    boxShadow: '0 0 10px rgba(16, 185, 129, 0.8)',
                  }}
                />

                {/* Center Dot */}
                <div className="w-5 h-5 bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-full shadow-lg" style={{ boxShadow: '0 0 20px rgba(218, 165, 32, 0.8)' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Left - Dua Panel */}
        <div className="info-panel backdrop-blur-md bg-black/80 border border-white/10 rounded-3xl p-8 shadow-2xl">
          <h3 className="text-yellow-500 text-lg font-semibold mb-5 text-center tracking-wide font-serif">Dua</h3>
          <div className="text-center">
            <div className="text-white text-lg leading-relaxed mb-4 font-light" style={{ direction: 'rtl', fontFamily: 'Decotype Thuluth, serif' }}>
              {duas?.[currentDuaIndex]?.arabic || 'Loading...'}
            </div>
            <div className="text-white/60 text-xs italic leading-relaxed font-light">
              {duas?.[currentDuaIndex]?.english || ''}
            </div>
          </div>
        </div>

        {/* Bottom Right - Next Prayer Countdown */}
        <div className="info-panel backdrop-blur-md bg-black/80 border border-white/10 rounded-3xl p-8 shadow-2xl">
          <h3 className="text-yellow-500 text-lg font-semibold mb-5 text-center tracking-wide font-serif">Next Prayer</h3>
          <SimplePrayerCountdownCard
            prayerTimes={prayerTimes}
            render={({ countdown, nextPrayer }) => (
              <div className="text-center">
                <div className="text-yellow-500 text-2xl font-light mb-2 font-serif">
                  {countdown}
                </div>
                <div className="text-white/60 text-xs uppercase tracking-wider">
                  Until {nextPrayer}
                </div>
              </div>
            )}
          />
          <div className="text-center mt-4 text-white/70 text-base">
            {settings.location?.split(',')[0] || 'Location not set'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LuxuryClockTemplate;